import React, { useEffect, useState, useReducer } from "react";
import {
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  Box,
  CircularProgress,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { FilterDrawer } from "../../../../components/FilterDrawer";
import { LoadingPlanSteps } from "../../CreateLoadingPlanPage";
import { useLoadingPlan } from "../../../../contexts/LoadingPlanContext";
import { LoadingPlanResponse, loadingPlansService, partsService } from "../../../../services";
import { LoadingPlanStatus } from "../../../../types/loadingPlan";
import "./Step1.scss";
import { currentLoadingPlanDefaultState } from "../../../../contexts/LoadingPlanContext/LoadingPlanContext";
import { TableComponent } from "../../../../components/ui/TableComponent/TableComponent";

// Types for reducers
type Part = LoadingPlanResponse['notIncludedParts'][0];

enum SortingField {
  NAME = 'name',
  MARK = 'mark',
  TYPE = 'type',
  SUB_TYPE = 'sub_type',
  WEIGHT = 'weight',
  SEQUENCE = 'sequence'
}

enum SortingOrder {
  ASC = 'asc',
  DESC = 'desc'
}

interface NotIncludedState {
  parts: Part[];
  selectedTypes: string[];
  // Sent as is: the server resolves the marks it matches (there can be thousands, too many for a URL).
  markMask: string;
  sequenceFrom: number | null;
  sequenceTo: number | null;
  page: number;
  rowsPerPage: number;
  selectedIndices: number[];
  selectAll: boolean;
  total: number;
  loading: boolean;
  sortBy: SortingField | null;
  sortOrder: SortingOrder;
}

interface IncludedState {
  parts: Part[];
  page: number;
  rowsPerPage: number;
  selectedIndices: number[];
  selectAll: boolean;
  total: number;
  loading: boolean;
  sortBy: SortingField | null;
  sortOrder: SortingOrder;
}

type NotIncludedAction =
  | { type: 'SET_PARTS'; payload: { parts: Part[], total: number } }
  | { type: 'SET_SELECTED_TYPES'; payload: string[] }
  | { type: 'SET_MARK_MASK'; payload: string }
  | { type: 'SET_SEQUENCE_RANGE'; payload: { from: number | null; to: number | null } }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_ROWS_PER_PAGE'; payload: number }
  | { type: 'TOGGLE_SELECT'; payload: number }
  | { type: 'TOGGLE_SELECT_ALL'; payload: number[] }
  | { type: 'SET_SELECT_ALL'; payload: boolean }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SORT'; payload: { sortBy: SortingField | null, sortOrder: SortingOrder } }
  | { type: 'REMOVE_PARTS'; payload: number[] };

type IncludedAction =
  | { type: 'ADD_PARTS'; payload: Part[] }
  | { type: 'SET_PARTS'; payload: { parts: Part[], total: number } }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_ROWS_PER_PAGE'; payload: number }
  | { type: 'TOGGLE_SELECT'; payload: number[] }
  | { type: 'TOGGLE_SELECT_ALL'; payload: number[] }
  | { type: 'SET_SELECT_ALL'; payload: boolean }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SORT'; payload: { sortBy: SortingField | null, sortOrder: SortingOrder } }
  | { type: 'REMOVE_PARTS'; payload: number[] };

// Reducers
function notIncludedReducer(state: NotIncludedState, action: NotIncludedAction): NotIncludedState {
  switch (action.type) {
    case 'SET_PARTS':
      return { ...state, parts: action.payload.parts, total: action.payload.total };
    case 'SET_SELECTED_TYPES':
      return { ...state, selectedTypes: action.payload, page: 0 };
    case 'SET_MARK_MASK':
      return { ...state, markMask: action.payload, page: 0 };
    case 'SET_SEQUENCE_RANGE':
      return {
        ...state,
        sequenceFrom: action.payload.from,
        sequenceTo: action.payload.to,
        page: 0
      };
    case 'SET_PAGE':
      return { ...state, page: action.payload, selectAll: false };
    case 'SET_ROWS_PER_PAGE':
      return { ...state, rowsPerPage: action.payload, page: 0, selectAll: false };
    case 'TOGGLE_SELECT':
      return {
        ...state,
        selectedIndices: state.selectedIndices.includes(action.payload)
          ? state.selectedIndices.filter(i => i !== action.payload)
          : [...state.selectedIndices, action.payload]
      };
    case 'TOGGLE_SELECT_ALL':
      if (state.selectAll) {
        return {
          ...state,
          selectedIndices: state.selectedIndices.filter(i => !action.payload.includes(i)),
          selectAll: false
        };
      } else {
        return {
          ...state,
          selectedIndices: Array.from(new Set([...state.selectedIndices, ...action.payload])),
          selectAll: true
        };
      }
    case 'SET_SELECT_ALL':
      return { ...state, selectAll: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_SORT':
      return { ...state, sortBy: action.payload.sortBy, sortOrder: action.payload.sortOrder, page: 0 };
    case 'CLEAR_SELECTION':
      return { ...state, selectedIndices: [], selectAll: false };
    case 'REMOVE_PARTS':
      return {
        ...state,
        parts: state.parts.filter((_, index) => !action.payload.includes(index)),
        selectedIndices: [],
        selectAll: false,
        page: 0
      };
    default:
      return state;
  }
}

function includedReducer(state: IncludedState, action: IncludedAction): IncludedState {
  switch (action.type) {
    case 'ADD_PARTS':
      return {
        ...state,
        parts: [...state.parts, ...action.payload]
      };
    case 'SET_PARTS':
      return { ...state, parts: action.payload.parts, total: action.payload.total };
    case 'SET_PAGE':
      return { ...state, page: action.payload, selectAll: false };
    case 'SET_ROWS_PER_PAGE':
      return { ...state, rowsPerPage: action.payload, page: 0, selectAll: false };
    case 'TOGGLE_SELECT':
      const newIndices = action.payload;
      const allSelected = newIndices.every(i => state.selectedIndices.includes(i));
      if (allSelected) {
        return {
          ...state,
          selectedIndices: state.selectedIndices.filter(i => !newIndices.includes(i))
        };
      } else {
        return {
          ...state,
          selectedIndices: Array.from(new Set([...state.selectedIndices, ...newIndices]))
        };
      }
    case 'TOGGLE_SELECT_ALL':
      if (state.selectAll) {
        return {
          ...state,
          selectedIndices: state.selectedIndices.filter(i => !action.payload.includes(i)),
          selectAll: false
        };
      } else {
        return {
          ...state,
          selectedIndices: Array.from(new Set([...state.selectedIndices, ...action.payload])),
          selectAll: true
        };
      }
    case 'SET_SELECT_ALL':
      return { ...state, selectAll: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_SORT':
      return { ...state, sortBy: action.payload.sortBy, sortOrder: action.payload.sortOrder, page: 0 };
    case 'CLEAR_SELECTION':
      return { ...state, selectedIndices: [], selectAll: false };
    case 'REMOVE_PARTS':
      return {
        ...state,
        parts: state.parts.filter((_, index) => !action.payload.includes(index)),
        selectedIndices: [],
        selectAll: false,
        page: 0
      };
    default:
      return state;
  }
}

interface Step1Props {
  setCurrentStep: (step: LoadingPlanSteps) => void;
  setUploadedFile: (file: File | null) => void;
  setShowStepFooter: (show: boolean) => void;
}

const Step1: React.FC<Step1Props> = (props: Step1Props) => {
  const { setShowStepFooter } = props;
  const { currentLoadingPlan, updateCurrentLoadingPlan } = useLoadingPlan();
  const [showPartsSection, setShowPartsSection] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [loadPlanName, setLoadPlanName] = useState<string>(currentLoadingPlan.title || "");
  const [shipDate, setShipDate] = useState<string>(currentLoadingPlan.shipDate || "");
  const [isUploadingIfc, setIsUploadingIfc] = useState(false);
  const [ifcError, setIfcError] = useState<string | null>(null);
  const [ifcSuccess, setIfcSuccess] = useState<string | null>(null);
  const [availablePartTypes, setAvailablePartTypes] = useState<string[]>([]);
  const [availablePartMarks, setAvailablePartMarks] = useState<string[]>([]);
  const [availableSequenceRange, setAvailableSequenceRange] = useState<{ min?: number; max?: number }>({});
  const [weights, setWeights] = useState<{ total: number; loaded: number }>({ total: 0, loaded: 0 });
  const dateInputRef = React.useRef<HTMLInputElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const pollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);


  // Initialize reducers
  const [notIncludedState, dispatchNotIncluded] = useReducer(notIncludedReducer, {
    parts: [],
    selectedTypes: [],
    markMask: '',
    sequenceFrom: null,
    sequenceTo: null,
    page: 0,
    rowsPerPage: 10,
    selectedIndices: [],
    selectAll: false,
    total: 0,
    loading: false,
    sortBy: SortingField.NAME,
    sortOrder: SortingOrder.ASC,
  });

  const [includedState, dispatchIncluded] = useReducer(includedReducer, {
    parts: [],
    page: 0,
    rowsPerPage: 10,
    selectedIndices: [],
    selectAll: false,
    total: 0,
    loading: false,
    sortBy: SortingField.NAME,
    sortOrder: SortingOrder.ASC,
  });

  // Filter parts by selected types (only for Not Included table)
  const filteredNotIncludedParts = React.useMemo(() => {
    return notIncludedState.parts.map((part, idx) => ({ part, originalIndex: idx }));
  }, [notIncludedState.parts]);

  const toggleNotIncludedRow = (index: number) => {
    dispatchNotIncluded({ type: 'TOGGLE_SELECT', payload: index });
  };

  const toggleAllNotIncluded = () => {
    // Since data is paginated, use local indices (0 to current page length)
    const currentPageIndices = Array.from({ length: filteredNotIncludedParts.length }, (_, i) => i);

    dispatchNotIncluded({ type: 'TOGGLE_SELECT_ALL', payload: currentPageIndices });
  };

  const toggleAllIncluded = () => {
    const startIndex = includedState.page * includedState.rowsPerPage;
    const endIndex = Math.min(startIndex + includedState.rowsPerPage, includedState.parts.length);
    const currentPageIndices = Array.from({ length: endIndex - startIndex }, (_, i) => startIndex + i);

    dispatchIncluded({ type: 'TOGGLE_SELECT_ALL', payload: currentPageIndices });
  };

  const handleNotIncludedSort = (field: SortingField) => {
    const newOrder = notIncludedState.sortBy === field && notIncludedState.sortOrder === 'asc' ? SortingOrder.DESC : SortingOrder.ASC;
    dispatchNotIncluded({ type: 'SET_SORT', payload: { sortBy: field, sortOrder: newOrder } });
  };

  const handleIncludedSort = (field: SortingField) => {
    const newOrder = includedState.sortBy === field && includedState.sortOrder === 'asc' ? SortingOrder.DESC : SortingOrder.ASC;
    dispatchIncluded({ type: 'SET_SORT', payload: { sortBy: field, sortOrder: newOrder } });
  };

  // Centralized fetch function for both tables
  const fetchBothTables = React.useCallback(async (options?: {
    skipIncluded?: boolean;
    skipNotIncluded?: boolean;
  }) => {
    if (!currentLoadingPlan?.id) return;

    const fetchParamsBase = {
      loadingPlanId: currentLoadingPlan.id,
    };

    const promises: Promise<any>[] = [];
    const fetchConfigs: Array<'included' | 'notIncluded'> = [];

    if (!options?.skipIncluded) {
      dispatchIncluded({ type: 'SET_LOADING', payload: true });
      fetchConfigs.push('included');
      promises.push(
        partsService.fetchParts({
          ...fetchParamsBase,
          isIncluded: true,
          limit: includedState.rowsPerPage,
          offset: includedState.page * includedState.rowsPerPage,
          sortBy: includedState.sortBy || undefined,
          sortOrder: includedState.sortBy ? (includedState.sortOrder === 'asc' ? 'ASC' : 'DESC') : undefined,
        })
      );
    }

    if (!options?.skipNotIncluded) {
      dispatchNotIncluded({ type: 'SET_LOADING', payload: true });
      fetchConfigs.push('notIncluded');

      promises.push(
        partsService.fetchParts({
          ...fetchParamsBase,
          isIncluded: false,
          limit: notIncludedState.rowsPerPage,
          offset: notIncludedState.page * notIncludedState.rowsPerPage,
          type: notIncludedState.selectedTypes.length > 0 ? notIncludedState.selectedTypes : undefined,
          markMask: notIncludedState.markMask.trim() || undefined,
          sequenceFrom: notIncludedState.sequenceFrom ?? undefined,
          sequenceTo: notIncludedState.sequenceTo ?? undefined,
          sortBy: notIncludedState.sortBy || undefined,
          sortOrder: notIncludedState.sortBy ? (notIncludedState.sortOrder === 'asc' ? 'ASC' : 'DESC') : undefined,
        })
      );
    }

    try {
      const results = await Promise.all(promises);

      let resultIndex = 0;
      fetchConfigs.forEach((config) => {
        if (config === 'included') {
          dispatchIncluded({ type: 'SET_PARTS', payload: { parts: results[resultIndex].parts, total: results[resultIndex].total } });
          resultIndex++;
        } else if (config === 'notIncluded') {
          dispatchNotIncluded({ type: 'SET_PARTS', payload: { parts: results[resultIndex].parts, total: results[resultIndex].total } });
          resultIndex++;
        }
      });
    } catch (error) {
      console.error('Failed to fetch parts:', error);
    } finally {
      if (!options?.skipIncluded) {
        dispatchIncluded({ type: 'SET_LOADING', payload: false });
      }
      if (!options?.skipNotIncluded) {
        dispatchNotIncluded({ type: 'SET_LOADING', payload: false });
      }
    }
  }, [currentLoadingPlan?.id, includedState.page, includedState.rowsPerPage, includedState.sortBy, includedState.sortOrder, notIncludedState.page, notIncludedState.rowsPerPage, notIncludedState.selectedTypes, notIncludedState.markMask, notIncludedState.sequenceFrom, notIncludedState.sequenceTo, notIncludedState.sortBy, notIncludedState.sortOrder]);

  const handleAddSelected = async () => {
    if (!currentLoadingPlan?.id) return;

    const validSelections = notIncludedState.selectedIndices.filter(index => index < filteredNotIncludedParts.length);
    const partsToAdd = validSelections.map(index => filteredNotIncludedParts[index].part);

    if (partsToAdd.length === 0) return;

    try {
      // Use new backend endpoint to add parts
      const partIds = partsToAdd.map(p => p.id);
      await loadingPlansService.addParts(currentLoadingPlan.id, partIds);

      // Reset pages before fetching to avoid triggering useEffect
      dispatchIncluded({ type: 'SET_PAGE', payload: 0 });
      dispatchNotIncluded({ type: 'SET_PAGE', payload: 0 });

      // Clear selection
      dispatchNotIncluded({ type: 'CLEAR_SELECTION' });

      // Fetch updated data from page 0
      await fetchBothTables();
    } catch (error) {
      console.error('Failed to add parts:', error);
    }
  };

  const handleAddAll = async () => {
    if (!currentLoadingPlan?.id) return;

    try {
      // Use new backend endpoint to add all parts with filters
      const filters: {
        type?: string[];
        markMask?: string;
        sequenceFrom?: number;
        sequenceTo?: number;
      } = {};
      if (notIncludedState.selectedTypes.length > 0) {
        filters.type = notIncludedState.selectedTypes;
      }
      if (notIncludedState.markMask.trim() !== '') {
        filters.markMask = notIncludedState.markMask.trim();
      }
      if (notIncludedState.sequenceFrom != null) {
        filters.sequenceFrom = notIncludedState.sequenceFrom;
      }
      if (notIncludedState.sequenceTo != null) {
        filters.sequenceTo = notIncludedState.sequenceTo;
      }

      await loadingPlansService.addAllParts(currentLoadingPlan.id, filters);

      // Reset pages before fetching to avoid triggering useEffect
      dispatchIncluded({ type: 'SET_PAGE', payload: 0 });
      dispatchNotIncluded({ type: 'SET_PAGE', payload: 0 });

      // Clear selection
      dispatchNotIncluded({ type: 'CLEAR_SELECTION' });

      // Fetch updated data from page 0
      await fetchBothTables();
    } catch (error) {
      console.error('Failed to add all parts:', error);
    }
  };

  const handleRemoveSelected = async () => {
    if (!currentLoadingPlan?.id) return;

    if (includedState.selectedIndices.length === 0) return;

    try {
      // Get the part IDs to remove based on selected indices
      // Each selected index removes 1 qty from that part
      const partIdsToRemove = includedState.selectedIndices
        .map(index => includedState.parts[index]?.id)
        .filter(Boolean);

      // Use backend endpoint to remove parts (removes 1 qty per part ID)
      await loadingPlansService.removeParts(currentLoadingPlan.id, partIdsToRemove);

      // Reset pages before fetching to avoid triggering useEffect
      dispatchIncluded({ type: 'SET_PAGE', payload: 0 });
      dispatchNotIncluded({ type: 'SET_PAGE', payload: 0 });

      // Clear selection
      dispatchIncluded({ type: 'CLEAR_SELECTION' });

      // Fetch updated data from page 0
      await fetchBothTables();
    } catch (error) {
      console.error('Failed to remove parts:', error);
    }
  };

  const handleRemoveAll = async () => {
    if (!currentLoadingPlan?.id) return;

    if (includedState.total === 0) return;

    try {
      // Use new backend endpoint to remove all parts
      await loadingPlansService.removeAllParts(currentLoadingPlan.id);

      // Reset pages before fetching to avoid triggering useEffect
      dispatchIncluded({ type: 'SET_PAGE', payload: 0 });
      dispatchNotIncluded({ type: 'SET_PAGE', payload: 0 });

      // Clear selection
      dispatchIncluded({ type: 'CLEAR_SELECTION' });

      // Fetch updated data from page 0
      await fetchBothTables();
    } catch (error) {
      console.error('Failed to remove all parts:', error);
    }
  };

  const handleNotIncludedPageChange = (_event: unknown, newPage: number) => {
    dispatchNotIncluded({ type: 'SET_PAGE', payload: newPage });
  };

  const handleNotIncludedRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatchNotIncluded({ type: 'SET_ROWS_PER_PAGE', payload: parseInt(event.target.value, 10) });
  };

  const handleIncludedPageChange = (_event: unknown, newPage: number) => {
    dispatchIncluded({ type: 'SET_PAGE', payload: newPage });
  };

  const handleIncludedRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatchIncluded({ type: 'SET_ROWS_PER_PAGE', payload: parseInt(event.target.value, 10) });
  };

  const handleLoadPlanNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLoadPlanName(value);
    updateCurrentLoadingPlan({ title: value });
  };

  const handleShipDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setShipDate(value);
    updateCurrentLoadingPlan({ shipDate: value });
  };

  const handleUploadIfcFile = () => {
    // Trigger file input click
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let newPlanId = undefined;

    if (!currentLoadingPlan?.id) {
      const newPlan = await loadingPlansService.createLoadingPlan({
        title: loadPlanName,
        shipDate: new Date(shipDate).toISOString(),
        trailerLength: currentLoadingPlanDefaultState.trailerLength as number,
        trailerMaxWidth: currentLoadingPlanDefaultState.trailerMaxWidth as number,
        trailerMaxWeight: currentLoadingPlanDefaultState.trailerMaxWeight as number,
        trailerMaxHeight: currentLoadingPlanDefaultState.trailerMaxHeight as number
      });
      updateCurrentLoadingPlan({ id: newPlan.id });
      newPlanId = newPlan.id;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingIfc(true);
    setIfcError(null);
    setIfcSuccess(null);

    try {
      const planId = currentLoadingPlan?.id as number || newPlanId as number;
      await partsService.uploadIfcFile(planId, file);

      // Start polling for status for all plans
      startPollingLoadingPlanStatus(planId);
    } catch (error) {
      console.error('Failed to upload IFC file:', error);
      setIsUploadingIfc(false);
    } finally {
      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const startPollingLoadingPlanStatus = (planId: number) => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const checkStatus = async () => {
      try {
        const loadingPlan = await loadingPlansService.fetchLoadingPlanById(planId);

        if (loadingPlan.status === LoadingPlanStatus.DRAFT) {
          // Status is draft, stop polling and show parts
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsUploadingIfc(false);
          updateCurrentLoadingPlan({ status: loadingPlan.status });
          setIfcSuccess('IFC file processed successfully. Parts have been extracted and are ready for selection.');
          setShowPartsSection(true);
          setShowStepFooter(true);
          fetchBothTables();
        } else if (loadingPlan.status === LoadingPlanStatus.FAILED) {
          // Stop polling on failure
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsUploadingIfc(false);
          updateCurrentLoadingPlan({ status: loadingPlan.status });
          setIfcError('IFC file processing failed. Please check the file and try upload again.');
        }
        // Continue polling for other statuses (IN_PROGRESS, etc.)
      } catch (error) {
        console.error('Failed to fetch loading plan status:', error);
      }
    };

    // Start initial check
    checkStatus();

    // Poll every 2 seconds
    pollingIntervalRef.current = setInterval(checkStatus, 2000);
  };

  useEffect(() => {
    if (!currentLoadingPlan?.id) return;

    fetchBothTables().then(() => {
      setShowPartsSection(true);
    });
  }, [currentLoadingPlan?.id]);

  useEffect(() => {
    fetchBothTables({ skipNotIncluded: true });
  }, [includedState.page, includedState.sortBy, includedState.sortOrder, includedState.rowsPerPage]);

  useEffect(() => {
    fetchBothTables({ skipIncluded: true });
  }, [notIncludedState.page, notIncludedState.sortBy, notIncludedState.sortOrder, notIncludedState.rowsPerPage, notIncludedState.selectedTypes, notIncludedState.markMask, notIncludedState.sequenceFrom, notIncludedState.sequenceTo, isUploadingIfc]);

  // Fetch available part types and marks
  useEffect(() => {
    if (!showPartsSection || !currentLoadingPlan?.id) return;

    const fetchFilters = async () => {
      try {
        const filters = await partsService.fetchFilters(currentLoadingPlan.id);
        setAvailablePartTypes(filters.types);
        setAvailablePartMarks(filters.marks);
        if (filters.minSequence !== undefined || filters.maxSequence !== undefined) {
          setAvailableSequenceRange({ min: filters.minSequence, max: filters.maxSequence });
        }
      } catch (error) {
        console.error('Failed to fetch filters:', error);
      }
    };

    fetchFilters();
  }, [showPartsSection, currentLoadingPlan?.id, isUploadingIfc]);

  // Fetch weights
  useEffect(() => {
    if (!currentLoadingPlan?.id) return;

    const fetchWeights = async () => {
      try {
        const weightsData = await partsService.fetchWeights(currentLoadingPlan.id);
        setWeights(weightsData);
      } catch (error) {
        console.error('Failed to fetch weights:', error);
      }
    };

    fetchWeights();
  }, [currentLoadingPlan?.id, includedState.parts, notIncludedState.parts, isUploadingIfc]);

  useEffect(() => {
    if (currentLoadingPlan?.id) {
      setLoadPlanName(currentLoadingPlan.title || "");
      const dateValue = currentLoadingPlan.shipDate
        ? new Date(currentLoadingPlan.shipDate).toISOString().split('T')[0]
        : "";
      setShipDate(dateValue);

      if (currentLoadingPlan.includedParts.length > 0 || currentLoadingPlan.notIncludedParts.length > 0) {
        setShowPartsSection(true);
        setShowStepFooter(true);
      }
    }
  }, [currentLoadingPlan.title, currentLoadingPlan.shipDate, currentLoadingPlan.includedParts, currentLoadingPlan.notIncludedParts, currentLoadingPlan?.id, setShowStepFooter]);

  // Plan reopened while its IFC extraction is still running: resume polling. Included parts mean
  // the IN_PROGRESS job is a nesting run instead, which CreateLoadingPlanPage polls on Step 3.
  const resumedIfcPollingRef = React.useRef(false);
  useEffect(() => {
    if (resumedIfcPollingRef.current || !currentLoadingPlan?.id) return;
    if (currentLoadingPlan.status !== LoadingPlanStatus.IN_PROGRESS || currentLoadingPlan.includedParts.length > 0) return;

    resumedIfcPollingRef.current = true;
    setIsUploadingIfc(true);
    startPollingLoadingPlanStatus(currentLoadingPlan.id);
  }, [currentLoadingPlan?.id, currentLoadingPlan.status, currentLoadingPlan.includedParts]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    dispatchNotIncluded({ type: 'CLEAR_SELECTION' });
  }, [notIncludedState.parts]);

  useEffect(() => {
    dispatchIncluded({ type: 'CLEAR_SELECTION' });
  }, [includedState.parts]);

  return (
    <div className="step1">
      <Paper className="form-card">
        {/* Step Title */}
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Step 1: Select Steel Parts for Loading
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Specify parts from Trimble Connect by job and sequence number
        </Typography>

        {/* Loading Plan Name */}
        <TextField
          label="Loading Plan"
          placeholder="Name"
          fullWidth
          variant="outlined"
          value={loadPlanName}
          onChange={handleLoadPlanNameChange}
        />

        {/* Planned Ship Date */}
        <TextField
          label="Planned Ship Date"
          placeholder="MM/DD/YYYY"
          type="date"
          variant="outlined"
          className="ship-date-input"
          value={shipDate}
          onChange={handleShipDateChange}
          inputRef={dateInputRef}
          InputLabelProps={{
            shrink: true,
          }}
        />

        {/* Tip Alert */}
        <Alert severity="info">
          Tip: The system will automatically fetch IFC models and extract geometry for all parts in
          the selected sequence.
        </Alert>

        {/* Divider */}
        <hr className="divider" />

        {/* IFC URL */}
        <TextField
          label="IFC URL"
          placeholder="trimble://project/model.ifc"
          fullWidth
          variant="outlined"
          disabled
        />

        {/* Divider */}
        <hr className="divider" />

        {/* Buttons */}
        <Box className="button-row">
          <input
            ref={fileInputRef}
            type="file"
            accept=".ifc"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <Button
            variant="outlined"
            onClick={handleUploadIfcFile}
            disabled={!loadPlanName || !shipDate || isUploadingIfc}
          >
            {isUploadingIfc ? 'UPLOADING...' : 'UPLOAD IFC FILE'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled
          >
            LOAD PARTS FROM TRIMBLE CONNECT
          </Button>
        </Box>
      </Paper>

      {/* Loading Indicator */}
      {isUploadingIfc && (
        <Paper className="form-card" sx={{ mt: 2 }}>
          <Box className="generating-container">
            <CircularProgress />
            <Typography className="generating-text">Extracting Parts...</Typography>
          </Box>
        </Paper>
      )}

      {ifcError && (
        <Alert severity="error" onClose={() => setIfcError(null)} sx={{ mb: 2, mt: 2 }}>
          {ifcError}
        </Alert>
      )}
      {ifcSuccess && (
        <Alert severity="success" onClose={() => setIfcSuccess(null)} sx={{ mb: 2, mt: 2 }}>
          {ifcSuccess}
        </Alert>
      )}

      {/* Parts Section */}
      {!isUploadingIfc && showPartsSection && (
        <Paper className="form-card parts-section-container">
          {/* Filter and Reset */}
          <Box className="filter-reset-row">
            <Button
              variant="contained"
              color="primary"
              startIcon={<FilterListIcon />}
              onClick={() => setFilterDrawerOpen(true)}
            >
              FILTER
            </Button>
          </Box>

          {/* Not Included Table */}
          <TableComponent
            data={filteredNotIncludedParts.map(item => item.part)}
            columns={[
              {
                id: 'name',
                label: 'Name',
                sortable: true,
                onSort: () => handleNotIncludedSort(SortingField.NAME),
                sortDirection: notIncludedState.sortBy === SortingField.NAME ? notIncludedState.sortOrder : null,
                render: (part: Part) => part.name,
              },
              {
                id: 'mark',
                label: 'Mark',
                sortable: true,
                onSort: () => handleNotIncludedSort(SortingField.MARK),
                sortDirection: notIncludedState.sortBy === SortingField.MARK ? notIncludedState.sortOrder : null,
                render: (part: Part) => part.mark,
              },
              {
                id: 'sequence',
                label: 'Sequence',
                sortable: true,
                onSort: () => handleNotIncludedSort(SortingField.SEQUENCE),
                sortDirection: notIncludedState.sortBy === SortingField.SEQUENCE ? notIncludedState.sortOrder : null,
                render: (part: Part) => part.sequenceNumber,
              },
              {
                id: 'type',
                label: 'Type',
                sortable: true,
                onSort: () => handleNotIncludedSort(SortingField.TYPE),
                sortDirection: notIncludedState.sortBy === SortingField.TYPE ? notIncludedState.sortOrder : null,
                render: (part: Part) => part.type,
              },
              {
                id: 'subType',
                label: 'Sub Type',
                sortable: true,
                onSort: () => handleNotIncludedSort(SortingField.SUB_TYPE),
                sortDirection: notIncludedState.sortBy === SortingField.SUB_TYPE ? notIncludedState.sortOrder : null,
                render: (part: Part) => part.subType,
              },
              {
                id: 'weight',
                label: 'Weight, lbs',
                sortable: true,
                onSort: () => handleNotIncludedSort(SortingField.WEIGHT),
                sortDirection: notIncludedState.sortBy === SortingField.WEIGHT ? notIncludedState.sortOrder : null,
                render: (part: Part) => (part.weight != null ? part.weight.toFixed(2) : '-'),
              },
            ]}
            title="Not Included"
            subtitle={`${notIncludedState.selectedIndices.length} of ${notIncludedState.total} options selected`}
            selectable
            selectedIndices={notIncludedState.selectedIndices}
            onToggleRow={(index: number | number[]) => {
              if (typeof index === 'number') {
                toggleNotIncludedRow(index);
              }
            }}
            onToggleSelectAll={toggleAllNotIncluded}
            selectAll={notIncludedState.selectAll}
            paginated
            page={notIncludedState.page}
            rowsPerPage={notIncludedState.rowsPerPage}
            total={notIncludedState.total}
            onPageChange={handleNotIncludedPageChange}
            onRowsPerPageChange={handleNotIncludedRowsPerPageChange}
            loading={notIncludedState.loading}
            emptyMessage="No parts available"
            footerChips={[
              {
                label: "Selected",
                value: `${notIncludedState.selectedIndices.reduce((sum, i) => {
                  const item = filteredNotIncludedParts[i];
                  return sum + (item ? item.part.weight ?? 0 : 0);
                }, 0).toFixed(2)} lbs`
              },
              {
                label: "Total",
                value: `${(weights.total - weights.loaded).toFixed(2)} lbs`,
              },
            ]}
            className="table-container"
          />

          {/* Action Buttons */}
          <Box className="action-buttons">
            <Button
              variant="outlined"
              disabled={notIncludedState.selectedIndices.length === 0}
              onClick={handleAddSelected}
            >
              ADD
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={filteredNotIncludedParts.length === 0}
              onClick={handleAddAll}
            >
              ADD ALL
            </Button>
            <Button
              variant="outlined"
              disabled={includedState.selectedIndices.length === 0}
              onClick={handleRemoveSelected}
            >
              REMOVE
            </Button>
            <Button
              variant="outlined"
              disabled={includedState.parts.length === 0}
              onClick={handleRemoveAll}
            >
              REMOVE ALL
            </Button>
          </Box>

          {/* Included Table */}
          <TableComponent
            data={includedState.parts}
            columns={[
              {
                id: 'name',
                label: 'Name',
                sortable: true,
                onSort: () => handleIncludedSort(SortingField.NAME),
                sortDirection: includedState.sortBy === SortingField.NAME ? includedState.sortOrder : null,
                render: (part: Part) => part.name,
              },
              {
                id: 'mark',
                label: 'Mark',
                sortable: true,
                onSort: () => handleIncludedSort(SortingField.MARK),
                sortDirection: includedState.sortBy === SortingField.MARK ? includedState.sortOrder : null,
                render: (part: Part) => part.mark,
              },
              {
                id: 'sequence',
                label: 'Sequence',
                sortable: true,
                onSort: () => handleIncludedSort(SortingField.SEQUENCE),
                sortDirection: includedState.sortBy === SortingField.SEQUENCE ? includedState.sortOrder : null,
                render: (part: Part) => part.sequenceNumber,
              },
              {
                id: 'type',
                label: 'Type',
                sortable: true,
                onSort: () => handleIncludedSort(SortingField.TYPE),
                sortDirection: includedState.sortBy === SortingField.TYPE ? includedState.sortOrder : null,
                render: (part: Part) => part.type,
              },
              {
                id: 'subType',
                label: 'Sub Type',
                sortable: true,
                onSort: () => handleIncludedSort(SortingField.SUB_TYPE),
                sortDirection: includedState.sortBy === SortingField.SUB_TYPE ? includedState.sortOrder : null,
                render: (part: Part) => part.subType || '-',
              },
              {
                id: 'qty',
                label: 'Qty',
                render: (part: Part) => part.qty,
              },
              {
                id: 'weight',
                label: 'Weight, lbs',
                sortable: true,
                onSort: () => handleIncludedSort(SortingField.WEIGHT),
                sortDirection: includedState.sortBy === SortingField.WEIGHT ? includedState.sortOrder : null,
                render: (part: Part) => (part.weight != null ? part.weight.toFixed(2) : '-'),
              },
            ]}
            title="Included"
            subtitle={`${includedState.selectedIndices.length} of ${includedState.total} parts selected`}
            selectable
            selectedIndices={includedState.selectedIndices}
            onToggleRow={(index: number | number[]) => {
              if (typeof index === 'number') {
                dispatchIncluded({ type: 'TOGGLE_SELECT', payload: [index] });
              }
            }}
            onToggleSelectAll={toggleAllIncluded}
            selectAll={includedState.selectAll}
            paginated
            page={includedState.page}
            rowsPerPage={includedState.rowsPerPage}
            total={includedState.total}
            onPageChange={handleIncludedPageChange}
            onRowsPerPageChange={handleIncludedRowsPerPageChange}
            loading={includedState.loading}
            emptyMessage="No parts included"
            footerChips={[
              {
                label: "Selected",
                value: `${includedState.selectedIndices.reduce((sum, i) => {
                  const part = includedState.parts[i];
                  return sum + (part ? part.weight ?? 0 : 0);
                }, 0).toFixed(2)} lbs`,
              },
              {
                label: "Total",
                value: `${weights.loaded.toFixed(2)} lbs`,
              },
            ]}
            className="table-container"
          />
        </Paper>
      )}

      {/* Filter Drawer */}
      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        // Sequence Range
        sequenceFrom={notIncludedState.sequenceFrom}
        sequenceTo={notIncludedState.sequenceTo}
        minAvailableSequence={availableSequenceRange.min}
        maxAvailableSequence={availableSequenceRange.max}
        onSequenceRangeChange={(range) =>
          dispatchNotIncluded({ type: 'SET_SEQUENCE_RANGE', payload: range })
        }
        // Mark Mask
        availableMarks={availablePartMarks}
        markMask={notIncludedState.markMask}
        onMarkMaskChange={(mask) =>
          dispatchNotIncluded({ type: 'SET_MARK_MASK', payload: mask })
        }
        // Types
        availableTypes={availablePartTypes}
        selectedTypes={notIncludedState.selectedTypes}
        onTypeSelectionChange={(types) =>
          dispatchNotIncluded({ type: 'SET_SELECTED_TYPES', payload: types })
        }
      />
    </div>
  );
};

export { Step1 };
